/* eslint-disable @typescript-eslint/no-misused-promises */
import Head from "next/head";
import { Header } from "~/sections/Header";
import { type RouterOutputs, api } from "~/utils/api";
import { Calendar } from "react-calendar";
import { Fragment, type ReactNode, useState, useEffect } from "react";

import IconLeft from "~/assets/icon-left.svg";
import IconRight from "~/assets/icon-right.svg";

import dayjs from "dayjs";
import "dayjs/locale/cs";
import "dayjs/locale/vi";
import { Controller, useForm } from "react-hook-form";
import { cn } from "~/utils/cn";

import { loadStripe } from "@stripe/stripe-js";
import { env } from "~/env.mjs";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useMutation } from "@tanstack/react-query";
import { RenderError } from "~/components/RenderError";
import HealthImage from "~/assets/health.jpg";
import SchoolImage from "~/assets/school.jpg";
import GovernmentImage from "~/assets/government.jpg";
import IconCheck from "~/assets/icon-check.svg";
import IconActive from "~/assets/icon-active.svg";
import IconInactive from "~/assets/icon-inactive.svg";
import Image from "next/image";
import { getPrice } from "~/utils/metadata";
import { useTranslation } from "react-i18next";

const stripePromise = loadStripe(env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY);
dayjs.locale("cs");

const Button = (props: {
  type: "button" | "submit";
  children?: ReactNode;
  isLoading?: boolean;
}) => {
  return (
    <button
      type={props.type}
      className={cn(
        "btn font-bold uppercase",
        "disabled:cursor-not-allowed disabled:opacity-50"
      )}
      disabled={props.isLoading}
    >
      {props.isLoading ? "Načítání..." : props.children}
    </button>
  );
};

interface DateTimeData {
  startTime: string;
  duration: 15 | 30 | 45 | 60;
}

function DateTimePickerForm(props: {
  value: DateTimeData | undefined;
  purpose: ServiceData["purpose"];
  duration: 15 | 30 | 45 | 60;
  onBack: () => void;
  onSubmit: (data: DateTimeData) => void;
}) {
  const { t } = useTranslation();
  const [viewDate, setViewDate] = useState(new Date());
  const [activeDate, setActiveDate] = useState<Date | null>(viewDate);
  const [state, setState] = useState<DateTimeData | undefined>(props.value);

  const slotDays = api.events.slots.useQuery({
    purpose: props.purpose,
    duration: props.duration,
    startTime: dayjs(viewDate).startOf("month").toISOString(),
    endTime: dayjs(viewDate).endOf("month").toISOString(),
  });

  const slotDate = activeDate ? dayjs(activeDate).format("YYYY-MM-DD") : null;
  const slots = slotDate ? slotDays.data?.[slotDate] : [];

  return (
    <div>
      <h2 className="title-xx-large text-center text-white">
        {t("reservation.datetime.title")}
      </h2>

      <hr className="my-6 border-white/10" />

      <div className="grid gap-4 sm:grid-cols-[2fr,1fr]">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-[auto,1fr,auto] items-center p-2">
            <button
              type="button"
              className="p-1 text-brand-green-primary outline-none"
              onClick={() => {
                setViewDate(dayjs(viewDate).subtract(1, "month").toDate());
              }}
            >
              <IconLeft />
            </button>

            <button
              className="text-center text-lg font-bold capitalize text-text-primary outline-none"
              onClick={() => setViewDate(new Date())}
            >
              {dayjs(viewDate).format("MMMM YYYY")}
            </button>

            <button
              type="button"
              className="p-1 text-brand-green-primary outline-none"
              onClick={() => {
                setViewDate(dayjs(viewDate).add(1, "month").toDate());
              }}
            >
              <IconRight />
            </button>
          </div>
          <Calendar
            activeStartDate={viewDate}
            showNavigation={false}
            value={activeDate}
            locale={dayjs.locale()}
            formatDay={(_, date) => dayjs(date).format("D")}
            onChange={(value) => {
              if (value instanceof Date) {
                setActiveDate(value);
              }
            }}
            tileDisabled={({ date }) => {
              const slot = slotDays.data?.[dayjs(date).format("YYYY-MM-DD")];
              return !slot || slot.length === 0;
            }}
            onActiveStartDateChange={({ activeStartDate }) => {
              if (activeStartDate) {
                setViewDate(activeStartDate);
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <button
            className="p-2 text-center text-lg font-bold capitalize text-text-primary outline-none"
            onClick={() => setViewDate(activeDate ?? new Date())}
          >
            {dayjs(activeDate).format("dddd, DD. MM.")}
          </button>

          <div
            className={cn(
              "grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-1 overflow-y-auto sm:max-h-[30vh] sm:grid-cols-1",
              props.duration === 15 && "md:grid-cols-2"
            )}
          >
            {slots?.map((slot) => (
              <button
                key={slot.time}
                type="button"
                className={cn(
                  "border border-transparent bg-white/10 px-4 py-2 font-medium text-white transition-all hover:bg-white/20",
                  state?.startTime === slot.time &&
                    "border-brand-green-primary bg-bg-primary text-brand-green-primary hover:bg-bg-secondary"
                )}
                onClick={() =>
                  setState({ startTime: slot.time, duration: props.duration })
                }
              >
                {dayjs(slot.time).format("HH:mm")}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="h-6" />

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-2 font-bold uppercase text-brand-green-primary outline-none"
          onClick={props.onBack}
        >
          <IconLeft />
          <span>{t("reservation.back")}</span>
        </button>
        <button
          type="submit"
          className="btn font-bold uppercase"
          onClick={() => {
            if (!state) return;
            props.onSubmit(state);
          }}
        >
          {t("reservation.datetime.submit")}
        </button>
      </div>
    </div>
  );
}

export interface ServiceData {
  purpose: "health" | "government" | "school" | "other";
  description: string;
}

function PurposeList(props: {
  value: "health" | "government" | "school" | "other";
  onChange: (value: "health" | "government" | "school" | "other") => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <button
        className={cn(
          "flex items-center justify-between bg-white/10 text-left text-lg font-medium text-white transition-all hover:bg-white/[0.15]",
          props.value === "health" && "bg-white/20 hover:bg-white/[0.25]"
        )}
        type="button"
        onClick={() => props.onChange("health")}
      >
        <span className="flex items-center">
          <Image
            src={HealthImage}
            alt={t("reservation.service.purpose.health")}
            className="h-16 w-16"
          />
          <span className="px-5 py-4">
            {t("reservation.service.purpose.health")}
          </span>
        </span>
        {props.value === "health" && (
          <span className="px-5">
            <IconCheck />
          </span>
        )}
      </button>
      <button
        className={cn(
          "flex items-center justify-between bg-white/10 text-left text-lg font-medium text-white transition-all hover:bg-white/[0.15]",
          props.value === "government" && "bg-white/20 hover:bg-white/[0.25]"
        )}
        type="button"
        onClick={() => props.onChange("government")}
      >
        <span className="flex items-center">
          <Image
            src={GovernmentImage}
            alt={t("reservation.service.purpose.government")}
            className="h-16 w-16"
          />
          <span className="px-5 py-4">
            {t("reservation.service.purpose.government")}
          </span>
        </span>

        {props.value === "government" && (
          <span className="px-5">
            <IconCheck />
          </span>
        )}
      </button>
      <button
        className={cn(
          "flex items-center justify-between bg-white/10 text-left text-lg font-medium text-white transition-all hover:bg-white/[0.15]",
          props.value === "school" && "bg-white/20 hover:bg-white/[0.25]"
        )}
        type="button"
        onClick={() => props.onChange("school")}
      >
        <span className="flex items-center">
          <Image
            src={SchoolImage}
            alt={t("reservation.service.purpose.school")}
            className="h-16 w-16"
          />
          <span className="px-5 py-4">
            {t("reservation.service.purpose.school")}
          </span>
        </span>

        {props.value === "school" && (
          <span className="px-5">
            <IconCheck />
          </span>
        )}
      </button>
      <button
        className={cn(
          "flex items-center justify-between bg-white/10 text-left text-lg font-medium text-white transition-all hover:bg-white/[0.15]",
          props.value === "other" && "bg-white/20 hover:bg-white/[0.25]"
        )}
        type="button"
        onClick={() => props.onChange("other")}
      >
        <span className="flex items-center">
          <span className="h-16 w-16 bg-bg-primary" />
          <span className="px-5 py-4">
            {t("reservation.service.purpose.other")}
          </span>
        </span>

        {props.value === "other" && (
          <span className="px-5">
            <IconCheck />
          </span>
        )}
      </button>
    </div>
  );
}

function ServiceForm(props: {
  value: ServiceData | undefined;
  onSubmit: (data: ServiceData) => void;
}) {
  const form = useForm<ServiceData>({ defaultValues: props.value });
  const onSubmit = form.handleSubmit(props.onSubmit);
  const { t } = useTranslation();

  return (
    <form
      className="flex flex-col gap-8"
      onSubmit={(event) => void onSubmit(event)}
    >
      <div className="flex flex-col gap-2">
        <h2 className="title-xx-large text-center text-white">
          {t("reservation.service.title")}
        </h2>
        <p className="body-large text-center text-text-secondary">
          {t("reservation.service.description")}
        </p>
      </div>

      <Controller
        control={form.control}
        name="purpose"
        render={({ field }) => <PurposeList {...field} />}
      />

      <div className="flex flex-col gap-2">
        <label className="text-white/50">
          {t("reservation.service.input")}
        </label>
        <textarea
          className="bg-white/10 p-4 text-white outline-none"
          placeholder={t("reservation.service.input")}
          {...form.register("description")}
        />
      </div>

      <button type="submit" className="btn font-bold uppercase">
        {t("reservation.continue")}
      </button>
    </form>
  );
}

interface ContactData {
  callMethod: "phone" | "video";
  name: string;
  phone: string;
  email: string;
}

const ContactMethodSelect = (props: {
  value: "phone" | "video" | undefined;
  onChange: (value: "phone" | "video" | undefined) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-2">
      <button
        type="button"
        className={cn(
          "flex items-center justify-between bg-white/10 px-6 py-5 text-left text-lg font-medium text-text-primary transition-all hover:bg-white/[0.15]",
          props.value === "phone" && "bg-white/20 hover:bg-white/[0.25]"
        )}
        onClick={() => props.onChange("phone")}
      >
        <span>{t("reservation.contact.callMethod.phone")}</span>
        {props.value === "phone" && <IconCheck />}
      </button>
      <button
        type="button"
        className={cn(
          "flex items-center justify-between bg-white/10 px-6 py-5 text-left text-lg font-medium text-text-primary transition-all hover:bg-white/[0.15]",
          props.value === "video" && "bg-white/20 hover:bg-white/[0.25]"
        )}
        onClick={() => props.onChange("video")}
      >
        <span>{t("reservation.contact.callMethod.video")}</span>
        {props.value === "video" && <IconCheck />}
      </button>
    </div>
  );
};

function ContactForm(props: {
  value: ContactData | undefined;
  onSubmit: (data: ContactData) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const form = useForm<ContactData>({ defaultValues: props.value });
  const onSubmit = form.handleSubmit(props.onSubmit);

  return (
    <form onSubmit={(e) => void onSubmit(e)}>
      <h2 className="title-xx-large mb-12 text-center text-white">
        {t("reservation.contact.title")}
      </h2>

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm text-text-primary">
            {t("reservation.contact.callMethod.title")}
          </label>
          <Controller
            control={form.control}
            name="callMethod"
            render={({ field }) => <ContactMethodSelect {...field} />}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm text-text-primary">
            {t("reservation.contact.name")}
          </label>
          <input
            className="bg-white/10 px-6 py-5 text-lg font-medium text-text-primary outline-none"
            type="text"
            placeholder={t("reservation.contact.name")}
            {...form.register("name")}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm text-text-primary">
            {t("reservation.contact.phone")}
          </label>
          <input
            className="bg-white/10 px-6 py-5 text-lg font-medium text-text-primary outline-none"
            type="tel"
            placeholder={t("reservation.contact.phone")}
            {...form.register("phone")}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm text-text-primary">
            {t("reservation.contact.email")}
          </label>
          <input
            className="bg-white/10 px-6 py-5 text-lg font-medium text-text-primary outline-none"
            type="email"
            placeholder={t("reservation.contact.email")}
            {...form.register("email")}
          />
        </div>
      </div>

      <div className="h-6" />

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-2 font-bold uppercase text-brand-green-primary outline-none"
          onClick={props.onBack}
        >
          <IconLeft />
          <span>{t("reservation.back")}</span>
        </button>

        <Button type="submit" isLoading={form.formState.isSubmitting}>
          {t("reservation.contact.submit")}
        </Button>
      </div>
    </form>
  );
}

interface DurationData {
  duration: 15 | 30 | 45 | 60;
}

function DurationForm(props: {
  purpose: ServiceData["purpose"];
  value: DurationData | undefined;
  onBack: () => void;
  onSubmit: (data: DurationData) => void;
}) {
  const { t } = useTranslation();
  const durationRanges =
    props.purpose === "health"
      ? ([30, 45, 60] as const)
      : ([15, 30, 45, 60] as const);

  const [duration, setDuration] = useState<15 | 30 | 45 | 60 | undefined>(
    props.value?.duration
  );
  return (
    <div>
      <h2 className="title-xx-large mb-12 text-center text-white">
        {t("reservation.duration.title")}
      </h2>

      <div className="flex flex-col gap-1">
        {durationRanges.map((item) => (
          <button
            key={item}
            type="button"
            className={cn(
              "flex items-center justify-between bg-white/10 px-6 py-5 text-left text-lg font-medium text-text-primary transition-all hover:bg-white/[0.15]",
              duration === item && "bg-white/20 hover:bg-white/[0.25]"
            )}
            onClick={() => setDuration(item)}
          >
            <span className="flex items-center gap-2">
              <span>
                {item} {t("reservation.duration.minute")}
              </span>
              <span className="font-normal text-text-secondary">|</span>
              <span className="font-normal text-text-secondary">
                {getPrice(item)} Kč
              </span>
            </span>

            {duration === item && <IconCheck />}
          </button>
        ))}
      </div>

      <div className="h-6" />

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-2 font-bold uppercase text-brand-green-primary outline-none"
          onClick={props.onBack}
        >
          <IconLeft />
          <span>{t("reservation.back")}</span>
        </button>

        <button
          className="btn font-bold uppercase"
          onClick={() => {
            if (duration == null) return;
            props.onSubmit({ duration });
          }}
        >
          {t("reservation.continue")}
        </button>
      </div>
    </div>
  );
}

function PaymentFormStripe(props: { onBack: () => void }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const form = useForm();

  const mutate = useMutation(async () => {
    if (!stripe || !elements) return;

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/rezervace/callback`,
      },
    });

    if (error) throw new Error(error.message);
  });

  const onSubmit = form.handleSubmit(async () => {
    try {
      return await mutate.mutateAsync();
    } catch {
      // pass
    }
  });

  return (
    <form onSubmit={(e) => void onSubmit(e)}>
      <div className="min-h-[250px]">
        <PaymentElement />
      </div>

      <RenderError error={mutate.error} />

      <div className="h-6" />

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-2 font-bold uppercase text-brand-green-primary outline-none"
          onClick={props.onBack}
        >
          <IconLeft />
          <span>{t("reservation.back")}</span>
        </button>

        <Button type="submit" isLoading={form.formState.isSubmitting}>
          {t("reservation.payment.submit")}
        </Button>
      </div>
    </form>
  );
}

function PaymentForm(props: {
  paymentIntent: RouterOutputs["events"]["paymentIntent"];
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <div>
      <h2 className="title-xx-large mb-12 text-center text-white">
        {t("reservation.payment.title")}
      </h2>

      {props.paymentIntent && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: props.paymentIntent,
            locale: i18n.language === "vn" ? "vi" : "cs",
            appearance: {
              theme: "flat",
              variables: {
                fontFamily: "Inter, sans-serif",
                colorText: "#fff",
                colorBackground: "#3D5A66",
                borderRadius: "0px",
                focusBoxShadow: "none",
                focusOutline: "none",
              },
            },
          }}
        >
          <PaymentFormStripe onBack={props.onBack} />
        </Elements>
      )}
    </div>
  );
}

const STATES = [
  { state: "service" as const },
  { state: "duration" as const },
  { state: "date" as const },
  { state: "contact" as const },
  { state: "payment" as const },
];

type StateType = (typeof STATES)[number]["state"];

function getNextState(state: StateType) {
  const idx = STATES.findIndex((item) => item.state === state);
  return STATES[idx + 1]?.state ?? state;
}

function getPrevState(state: StateType) {
  const idx = STATES.findIndex((item) => item.state === state);
  return STATES[idx - 1]?.state ?? state;
}

function Progress(props: { state: StateType }) {
  const { t } = useTranslation();
  const activeIdx = STATES.findIndex((item) => item.state === props.state);

  return (
    <div
      className={cn(
        "bg-bg-secondary px-[25px] pb-[50px] pt-[25px] md:px-[50px]",
        "hidden grid-cols-[auto,1fr,auto,1fr,auto,1fr,auto,1fr,auto] items-center gap-1 sm:grid"
      )}
    >
      {STATES.map((item, idx) => (
        <Fragment key={idx}>
          <div
            className="relative flex flex-col items-center gap-2"
            key={item.state}
          >
            <span>
              {item.state === props.state ? (
                <IconActive />
              ) : idx <= activeIdx ? (
                <IconCheck />
              ) : (
                <IconInactive />
              )}
            </span>
            <span
              className={cn(
                "absolute top-full mt-2.5 whitespace-nowrap text-center text-sm font-bold text-text-primary",
                idx <= activeIdx
                  ? "text-brand-green-primary"
                  : "text-text-primary"
              )}
            >
              {t(`reservation.progress.${item.state}`)}
            </span>
          </div>

          {idx + 1 < STATES.length && (
            <span
              className={cn(
                "h-0.5 w-full",
                idx < activeIdx ? "bg-brand-green-primary" : "bg-text-secondary"
              )}
            />
          )}
        </Fragment>
      ))}
    </div>
  );
}

function Summary(props: {
  service: ServiceData | undefined;
  duration: DurationData | undefined;
  dateTime: DateTimeData | undefined;
  contact: ContactData | undefined;

  className?: string;
}) {
  const { t } = useTranslation();

  if (!props.service && !props.duration && !props.dateTime && !props.contact) {
    return null;
  }

  return (
    <div className={cn("bg-bg-secondary p-10", props.className)}>
      <div className="flex flex-col gap-8">
        {props.service && (
          <Fragment>
            <div className="flex flex-col gap-1">
              <h3 className="text-xs font-medium uppercase text-text-secondary">
                {t("reservation.summary.purpose")}
              </h3>
              <p className="text-lg font-bold text-text-primary">
                {
                  {
                    health: t("reservation.service.purpose.health"),
                    government: t("reservation.service.purpose.government"),
                    school: t("reservation.service.purpose.school"),
                    other: t("reservation.service.purpose.other"),
                  }[props.service.purpose]
                }
              </p>
            </div>

            {props.service.description && (
              <div className="flex flex-col gap-1">
                <h3 className="text-xs font-medium uppercase text-text-secondary">
                  {t("reservation.summary.description")}
                </h3>
                <p className="text-lg font-bold text-text-primary">
                  {props.service.description}
                </p>
              </div>
            )}
          </Fragment>
        )}

        {props.duration && (
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-medium uppercase text-text-secondary">
              {t("reservation.summary.duration")}
            </h3>
            <div className="flex flex-col text-lg font-bold text-text-primary">
              <span>
                {props.duration.duration} {t("reservation.duration.minute")}
              </span>
              <span>{getPrice(props.duration.duration)} Kč</span>
            </div>
          </div>
        )}

        {props.dateTime && (
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-medium uppercase text-text-secondary">
              {t("reservation.summary.datetime")}
            </h3>
            <div className="flex flex-col text-lg font-bold text-text-primary">
              <span>
                {dayjs(props.dateTime.startTime).format("dddd, DD. MM.")}
              </span>
              <span>
                {[
                  dayjs(props.dateTime.startTime),
                  dayjs(props.dateTime.startTime).add(
                    props.dateTime.duration,
                    "minutes"
                  ),
                ]
                  .map((date) => date.format("HH:mm"))
                  .join(" – ")}
              </span>
            </div>
          </div>
        )}

        {props.contact && (
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-medium uppercase text-text-secondary">
              {t("reservation.summary.contact")}
            </h3>
            <div className="flex flex-col text-lg font-bold text-text-primary">
              {
                {
                  phone: t("reservation.contact.callMethod.phone"),
                  video: t("reservation.contact.callMethod.video"),
                }[props.contact.callMethod]
              }
              {props.contact.name && <span>{props.contact.name}</span>}
              {props.contact.phone && <span>{props.contact.phone}</span>}
              {props.contact.email && <span>{props.contact.email}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Page() {
  const [view, setView] = useState<StateType>(STATES[0]!.state);

  const [service, setService] = useState<ServiceData | undefined>();
  const [duration, setDuration] = useState<DurationData | undefined>();
  const [dateTime, setDateTime] = useState<DateTimeData | undefined>();
  const [contact, setContact] = useState<ContactData | undefined>();

  const paymentIntent = api.events.paymentIntent.useMutation();

  const { i18n } = useTranslation();

  const dateLocale = i18n.language === "vn" ? "vi" : "cs";
  useEffect(() => {
    dayjs.locale(dateLocale);
  }, [dateLocale]);

  return (
    <>
      <Head>
        <title>Rigo | Rezervace</title>
      </Head>

      <Header variant="reservation" />

      <div className="h-[100px]" />

      <div className="flex items-start justify-center gap-4 ">
        <div className="flex max-w-[800px] flex-grow flex-col gap-4">
          <main className="bg-bg-secondary p-6 pt-12 md:px-[100px] md:py-[64px]">
            {view === "service" && (
              <ServiceForm
                value={service}
                onSubmit={(service) => {
                  setService(service);
                  setView(getNextState);
                }}
              />
            )}

            {view === "duration" && (
              <DurationForm
                purpose={service?.purpose ?? "other"}
                value={duration}
                onBack={() => setView(getPrevState)}
                onSubmit={(duration) => {
                  setDuration(duration);
                  setView(getNextState);
                }}
              />
            )}

            {view === "date" && (
              <DateTimePickerForm
                key={dateLocale}
                value={dateTime}
                purpose={service?.purpose ?? "other"}
                duration={duration?.duration ?? 30}
                onBack={() => setView(getPrevState)}
                onSubmit={(dateTime) => {
                  setDateTime(dateTime);
                  setView(getNextState);
                }}
              />
            )}

            {view === "contact" && (
              <ContactForm
                value={contact}
                onBack={() => setView(getPrevState)}
                onSubmit={async (contact) => {
                  setContact(contact);
                  if (service == null || duration == null || dateTime == null)
                    return;

                  await paymentIntent.mutateAsync({
                    ...service,
                    ...duration,
                    ...dateTime,
                    ...contact,
                    language: "cs",
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  });

                  setView("payment");
                }}
              />
            )}

            {view === "payment" && (
              <PaymentForm
                paymentIntent={paymentIntent.data ?? null}
                onBack={() => setView(getPrevState)}
              />
            )}
          </main>

          <Progress state={view} />
        </div>

        <Summary
          key={dateLocale}
          service={service}
          duration={duration}
          dateTime={dateTime}
          contact={contact}
          className="hidden max-w-[400px] flex-grow md:block"
        />
      </div>
    </>
  );
}
